import React from 'react';
import { inject, observer } from 'mobx-react';
import { AuthStore, RoutingStore } from 'stores';
import { useTranslation } from 'react-i18next';
import { Icon } from 'components';
import { Box, IconButton, List, ListItem, ListItemIcon, Typography } from '@mui/material';
import { Container, Draggable } from 'react-smooth-dnd';
import { HierarchyTypes, rearrangeArray, UserType } from 'types';
import styled from 'styled-components';
import { TreeContext } from './treeViewContainer';
import { theme } from 'config';
import { Tree } from '../../stores/tree';

export interface IDataHierarchyProps {
  authStore?:    AuthStore,
  routingStore?: RoutingStore,
}
export const DataHierarchy = inject('authStore', 'routingStore')(observer((props: IDataHierarchyProps): JSX.Element => {
  const { authStore, routingStore } = props;
  const { t }     = useTranslation();
  const tree:  Tree = React.useContext(TreeContext);

  const [unusedTypes, setUnusedTypes] = React.useState<HierarchyTypes[]>(
    authStore.getCurUser.user_type === UserType.Manager ? 
      Object.values(HierarchyTypes).filter(t => !tree.hierarchy.includes(t) && t !== HierarchyTypes.Company && t !== HierarchyTypes.Quiz) :
    authStore.getCurUser.user_type === UserType.Analyst ? 
      Object.values(HierarchyTypes).filter(t => !tree.hierarchy.includes(t) && t !== HierarchyTypes.Quiz) :
      Object.values(HierarchyTypes).filter(t => !tree.hierarchy.includes(t))
  );
  const [usedTypes,   setUsedTypes]   = React.useState<HierarchyTypes[]>(tree.hierarchy);

  const removeType = (type: HierarchyTypes) => {
    setUsedTypes(usedTypes.filter(t => t !== type));
    setUnusedTypes([...unusedTypes, type]);
    tree.setHierarchy(usedTypes.filter(t => t !== type));
  }

  const addType = (type: HierarchyTypes) => {
    setUnusedTypes(unusedTypes.filter(t => t !== type));
    setUsedTypes([...usedTypes, type]);
    tree.setHierarchy([...usedTypes, type]);
  }

  const rearrange = (old_idx: number, new_idx: number) => {
    if(old_idx === new_idx) return;
    
    setUsedTypes(rearrangeArray(usedTypes, old_idx, new_idx));
    tree.setHierarchy(rearrangeArray(usedTypes, old_idx, new_idx));
  }

  return (
    <Box minWidth="19rem" justifyContent='space-between' height='100%'>
      <Typography>{t('display_data')}</Typography>
      <Box 
        width          = '20rem'
        height         = '100%'
        sx             = {{background: 'white'}}
        display        = "flex"
        flexDirection  = "column"
        justifyContent = "space-between"
      >
        <List component='div' disablePadding>
          {/* Selected Data Types */}
          <Container onDrop={(args) => rearrange(args.removedIndex, args.addedIndex)}>
            {usedTypes.map(type => (
              <Draggable key={type}>
                <ListItem
                  secondaryAction={usedTypes.length > 1 ? 
                    <IconButton
                      edge       = 'end'
                      aria-label = 'remove-type'
                      onClick    = {() => removeType(type)}
                    >
                      <Icon size='size-16' iconId='remove' />
                    </IconButton>
                    : undefined
                  }
                >
                  <ListItemIcon>
                    <DragIcon
                      iconId    = 'drag-handle'
                      className = 'drag_handle'
                    />
                  </ListItemIcon>
                  <Typography>{t(type)}</Typography>
                </ListItem>
              </Draggable>
            ))}
          </Container>
        </List>
        {/* Unselected Data Types */}
        <List 
          disablePadding
          component = 'div'
          sx = {{minHeight: "2rem", background: theme.palette.grey[200], borderTop: `1px solid ${theme.palette.grey[300]}`}}>
          {unusedTypes.map(type => (
            <ListItem
              key             = {type}
              secondaryAction = {
                <IconButton
                  edge       = 'end'
                  aria-label = 'add-type'
                  onClick    = {() => addType(type)}
                >
                  <Icon size='size-16' iconId='add' />
                </IconButton>
              }
            >
              <Typography>{t(type)}</Typography>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
}));

const DragIcon = styled(Icon)`
  position:  absolute;
  left:      0.75rem;
  top:       50%;
  cursor:    grab;
  transform: translateY(-50%);

  svg {
    color: black;
  }

  &:active {
    cursor: grabbing;
  }
`;